import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Spinner,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import {
  X,
  User,
  BookOpen,
  CreditCard,
  Calendar,
  Play,
  FileText,
  Award,
  Percent,
  StickyNote,
  Search,
} from "lucide-react";
import MainModel from "../../../../components/Models/main-model";
import { closeStudentReportModel } from "../../../../modules/admin-modules/models/Actions";
import {
  studentPerformanceRequest,
  monthlyReportRequest,
  addStudentNoteRequest,
} from "../../../../modules/admin-modules/reports/Actions";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
  surface: brandPalette.cream,
  canvas: brandPalette.warmBeige,
  muted: "rgba(69, 11, 17, 0.62)",
};

const SectionCard = ({ title, children, icon: Icon }) => (
  <Box
    borderRadius="xl"
    overflow="hidden"
    border="1px solid"
    borderColor={C.border}
    bg={C.surface}
    boxShadow="0 4px 18px rgba(69, 11, 17, 0.06)"
  >
    <Flex align="center" gap={3} px={5} py={3} bg={C.primary} color="white">
      {Icon ? <Icon size={18} strokeWidth={2.2} /> : null}
      <Text fontWeight="800" fontSize="15px" letterSpacing="0.02em">
        {title}
      </Text>
    </Flex>
    <Box p={{ base: 4, md: 5 }} bg={C.canvas}>
      {children}
    </Box>
  </Box>
);

const StudentReport = ({ selectedData, student_id }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [paymentSearchTerm, setPaymentSearchTerm] = useState("");
  const [videoSearchTerm, setVideoSearchTerm] = useState("");
  const [newNote, setNewNote] = useState("");
  const paymentSearchRef = useRef("");
  const videoSearchRef = useRef("");

  const userData = useSelector((state) => state.reports.performance);
  const isLoading = useSelector((state) => state.reports.isLoading);
  const openUserData = useSelector((state) => state.model.isOpen18);
  const dispatch = useDispatch();

  const baseUrl = "https://asquera.com/asquera";
  const hwUrl = "https://api.edutopiaonline.com/public/homework/";

  const {
    payment = [],
    attendance = [],
    payment_exception,
    last_videos = [],
    homeworks = [],
    student_homeworks = [],
    exams = [],
    student_notes = [],
  } = userData || {};

  useEffect(() => {
    if (student_id) dispatch(studentPerformanceRequest(student_id));
  }, [student_id, dispatch]);

  const closeFunc = () => {
    dispatch(closeStudentReportModel());
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "attendance", label: "Attendance", icon: Calendar },
    { id: "videos", label: "Videos", icon: Play },
    { id: "exams", label: "Exams", icon: Award },
    { id: "homeworks", label: "Homeworks", icon: BookOpen },
    { id: "notes", label: "Notes", icon: StickyNote },
  ];

  const getExamStats = () => {
    if (!exams || exams.length === 0) return null;
    const firstExam = exams[0];
    return firstExam?.student_exam?.[0] || null;
  };

  const examStats = getExamStats();

  const filteredPayments = useMemo(() => {
    return payment.filter(
      (item) =>
        item.payment_subject?.name
          ?.toLowerCase()
          .includes(paymentSearchTerm.toLowerCase()) ||
        item.paid_amount?.toString().includes(paymentSearchTerm) ||
        new Date(item.created_at)
          .toLocaleDateString()
          .includes(paymentSearchTerm)
    );
  }, [payment, paymentSearchTerm]);

  const filteredVideos = useMemo(() => {
    return last_videos.filter(
      (item) =>
        item.video?.title
          ?.toLowerCase()
          .includes(videoSearchTerm.toLowerCase()) ||
        item.views_count?.toString().includes(videoSearchTerm) ||
        new Date(item.created_at)
          .toLocaleDateString()
          .includes(videoSearchTerm)
    );
  }, [last_videos, videoSearchTerm]);

  const statPalette = [
    { labelColor: C.secondary, valueColor: C.primary, iconColor: C.primary },
    { labelColor: C.muted, valueColor: C.secondary, iconColor: C.secondary },
    { labelColor: C.muted, valueColor: C.primary, iconColor: C.primary },
    { labelColor: C.secondary, valueColor: C.secondary, iconColor: C.secondary },
  ];

  const StatCard = ({ title, value, icon: Icon, tone = 0 }) => {
    const t = statPalette[tone % statPalette.length];
    return (
      <Box
        p={5}
        borderRadius="xl"
        bg={C.surface}
        border="1px solid"
        borderColor={C.border}
        boxShadow="sm"
        transition="box-shadow 0.2s"
        _hover={{ boxShadow: "0 8px 24px rgba(69, 11, 17, 0.08)" }}
      >
        <Flex align="center" justify="space-between" gap={3}>
          <Box>
            <Text fontSize="sm" fontWeight="700" color={t.labelColor} mb={1}>
              {title}
            </Text>
            <Text fontSize="2xl" fontWeight="800" color={t.valueColor}>
              {value}
            </Text>
          </Box>
          {Icon ? (
            <Box color={t.iconColor} opacity={0.85}>
              <Icon size={28} strokeWidth={2} />
            </Box>
          ) : null}
        </Flex>
      </Box>
    );
  };

  const ItemList = ({ items, renderItem, emptyMessage, showAll = false }) => {
    const slice =
      items && items.length > 0
        ? showAll
          ? items
          : items.slice(0, 5)
        : [];
    return (
      <VStack align="stretch" spacing={3}>
        {slice.length > 0 ? (
          slice.map((item, index) => (
            <Flex
              key={index}
              align="center"
              justify="space-between"
              gap={3}
              p={3}
              bg={C.surface}
              borderRadius="lg"
              border="1px solid"
              borderColor={C.border}
              flexWrap={{ base: "wrap", sm: "nowrap" }}
              _hover={{ borderColor: "rgba(91, 13, 22, 0.35)" }}
            >
              {renderItem(item, index)}
            </Flex>
          ))
        ) : (
          <Box textAlign="center" py={10} color={C.muted}>
            <Flex justify="center" mb={3}>
              <FileText size={48} strokeWidth={1.5} opacity={0.35} />
            </Flex>
            <Text fontWeight="600">{emptyMessage}</Text>
          </Box>
        )}
        {!showAll && items && items.length > 5 && (
          <Text fontSize="sm" color={C.muted} textAlign="center" mt={1}>
            +{items.length - 5} more items
          </Text>
        )}
      </VStack>
    );
  };

  const searchInputStyles = {
    bg: C.surface,
    borderColor: C.border,
    borderRadius: "lg",
    _hover: { borderColor: C.primary },
    _focusVisible: {
      borderColor: C.primary,
      boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.25)",
    },
  };

  const renderOverview = () => (
    <VStack align="stretch" spacing={6}>
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4}>
        <StatCard title="Total Payments" value={payment?.length || 0} icon={CreditCard} tone={0} />
        <StatCard title="Attendance" value={attendance?.length || 0} icon={Calendar} tone={1} />
        <StatCard title="Videos Watched" value={last_videos?.length || 0} icon={Play} tone={2} />
        <StatCard title="Exams Taken" value={exams?.length || 0} icon={Award} tone={3} />
      </Grid>

      {payment_exception && (
        <Flex
          align="center"
          gap={4}
          p={5}
          borderRadius="xl"
          bg="rgba(91, 13, 22, 0.06)"
          border="1px solid"
          borderColor={C.border}
        >
          <Percent size={26} color={C.primary} />
          <Box>
            <Text fontWeight="800" color={C.primary} fontSize="md">
              Payment exception
            </Text>
            <Text fontWeight="700" color={C.secondary} mt={1}>
              {payment_exception.name}
            </Text>
          </Box>
        </Flex>
      )}

      {examStats && (
        <SectionCard title="Latest exam performance" icon={Award}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
            <Box textAlign="center" p={4} bg={C.surface} borderRadius="lg" border="1px solid" borderColor={C.border}>
              <Text fontSize="2xl" fontWeight="800" color={C.primary}>
                {examStats.total_exams_questions || 0}
              </Text>
              <Text fontSize="sm" fontWeight="700" color={C.muted} mt={1}>
                Total questions
              </Text>
            </Box>
            <Box textAlign="center" p={4} bg={C.surface} borderRadius="lg" border="1px solid" borderColor={C.border}>
              <Text fontSize="2xl" fontWeight="800" color={C.secondary}>
                {examStats.total_right_answers || 0}
              </Text>
              <Text fontSize="sm" fontWeight="700" color={C.muted} mt={1}>
                Correct answers
              </Text>
            </Box>
            <Box textAlign="center" p={4} bg={C.surface} borderRadius="lg" border="1px solid" borderColor={C.border}>
              <Text fontSize="2xl" fontWeight="800" color={C.primary}>
                {examStats.percentage || 0}%
              </Text>
              <Text fontSize="sm" fontWeight="700" color={C.muted} mt={1}>
                Success rate
              </Text>
            </Box>
          </Grid>
        </SectionCard>
      )}

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        <SectionCard title="Recent homeworks" icon={BookOpen}>
          <Box maxH="360px" overflowY="auto">
            <ItemList
              showAll
              items={homeworks}
              renderItem={(homework) => (
                <>
                  <HStack spacing={3}>
                    <BookOpen size={16} color={C.primary} />
                    <Text fontWeight="700" color={C.secondary}>
                      {homework.name}
                    </Text>
                  </HStack>
                  <Badge
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    bg={homework.active ? "rgba(34, 84, 61, 0.12)" : "rgba(69, 11, 17, 0.08)"}
                    color={homework.active ? "#1e5c3a" : C.secondary}
                  >
                    {homework.active ? "Active" : "Completed"}
                  </Badge>
                </>
              )}
              emptyMessage="No homework assignments found"
            />
          </Box>
        </SectionCard>

        <SectionCard title="Recent videos" icon={Play}>
          <ItemList
            items={last_videos}
            renderItem={(video) => (
              <>
                <HStack spacing={3} align="start">
                  <Play size={16} color={C.primary} style={{ marginTop: 2 }} />
                  <Text fontWeight="700" color={C.secondary}>
                    {video.video?.title}
                  </Text>
                </HStack>
                <Badge
                  borderRadius="full"
                  px={2}
                  py={0.5}
                  bg={video.seen ? "rgba(34, 84, 61, 0.12)" : "rgba(161, 98, 7, 0.15)"}
                  color={video.seen ? "#1e5c3a" : "#7a4a00"}
                >
                  {video.seen ? "Watched" : "Pending"}
                </Badge>
              </>
            )}
            emptyMessage="No videos watched yet"
          />
        </SectionCard>
      </Grid>
    </VStack>
  );

  const renderPayments = () => {
    const handlePaymentSearch = () => {
      setPaymentSearchTerm(paymentSearchRef.current);
    };
    const handlePaymentClear = () => {
      paymentSearchRef.current = "";
      setPaymentSearchTerm("");
    };

    return (
      <SectionCard title="Payment history" icon={CreditCard}>
        <VStack align="stretch" spacing={3} mb={4}>
          <HStack spacing={2} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup flex={1} minW={0}>
              <InputLeftElement pointerEvents="none" h="full">
                <Search size={18} color={C.muted} />
              </InputLeftElement>
              <Input
                pl={10}
                placeholder="Search by subject, amount, or date…"
                defaultValue=""
                onChange={(e) => {
                  paymentSearchRef.current = e.target.value;
                }}
                onKeyDown={(e) => e.key === "Enter" && handlePaymentSearch()}
                {...searchInputStyles}
              />
            </InputGroup>
            <Button
              bg={C.primary}
              color="white"
              borderRadius="lg"
              px={5}
              _hover={{ bg: C.secondary }}
              onClick={handlePaymentSearch}
            >
              Search
            </Button>
            {paymentSearchTerm ? (
              <Button variant="outline" borderColor={C.border} color={C.secondary} borderRadius="lg" onClick={handlePaymentClear}>
                Clear
              </Button>
            ) : null}
          </HStack>
          {paymentSearchTerm ? (
            <Text fontSize="sm" color={C.muted} fontWeight="600">
              Found {filteredPayments.length} payment{filteredPayments.length !== 1 ? "s" : ""} for &quot;{paymentSearchTerm}&quot;
            </Text>
          ) : null}
        </VStack>

        <Box maxH="384px" overflowY="auto">
          <ItemList
            items={filteredPayments}
            renderItem={(paymentItem) => (
              <>
                <Box>
                  <Text fontWeight="800" color={C.secondary}>
                    {paymentItem.payment_subject?.name}
                  </Text>
                  <Text fontSize="sm" color={C.muted} mt={1}>
                    {new Date(paymentItem.created_at).toLocaleDateString()}
                  </Text>
                </Box>
                <Box textAlign="right">
                  <Text fontWeight="800" color="#1e5c3a">
                    EGP {paymentItem.paid_amount}
                  </Text>
                  {paymentItem.remaining_amount !== 0 && (
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color={paymentItem.remaining_amount > 0 ? "#9b2c2c" : "#1e5c3a"}
                    >
                      {paymentItem.remaining_amount > 0 ? "Due: " : "Credit: "}
                      EGP {Math.abs(paymentItem.remaining_amount)}
                    </Text>
                  )}
                </Box>
              </>
            )}
            emptyMessage={paymentSearchTerm ? "No payments match your search" : "No payment records found"}
            showAll
          />
        </Box>
      </SectionCard>
    );
  };

  const renderAttendance = () => (
    <SectionCard title="Attendance records" icon={Calendar}>
      <ItemList
        items={attendance}
        renderItem={(attendanceItem) => (
          <>
            <Box>
              <Text fontWeight="800" color={C.secondary}>
                {attendanceItem.lecture?.title}
              </Text>
              <Text fontSize="sm" color={C.muted} mt={1}>
                {attendanceItem.actual_time
                  ? new Date(attendanceItem.actual_time).toLocaleString()
                  : new Date(attendanceItem.created_at).toLocaleString()}
              </Text>
            </Box>
            <HStack spacing={2}>
              <Badge
                borderRadius="full"
                px={2}
                py={0.5}
                bg={attendanceItem.attend ? "rgba(34, 84, 61, 0.12)" : "rgba(155, 44, 44, 0.12)"}
                color={attendanceItem.attend ? "#1e5c3a" : "#9b2c2c"}
              >
                {attendanceItem.attend ? "Present" : "Absent"}
              </Badge>
              {attendanceItem.parent_comment ? (
                <Text fontSize="xs" color={C.muted} title={attendanceItem.parent_comment}>
                  Parent note
                </Text>
              ) : null}
            </HStack>
          </>
        )}
        emptyMessage="No attendance records found"
        showAll
      />
    </SectionCard>
  );

  const renderVideos = () => {
    const handleVideoSearch = () => {
      setVideoSearchTerm(videoSearchRef.current);
    };
    const handleVideoClear = () => {
      videoSearchRef.current = "";
      setVideoSearchTerm("");
    };

    return (
      <SectionCard title="Video watch history" icon={Play}>
        <VStack align="stretch" spacing={3} mb={4}>
          <HStack spacing={2} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup flex={1} minW={0}>
              <InputLeftElement pointerEvents="none" h="full">
                <Search size={18} color={C.muted} />
              </InputLeftElement>
              <Input
                pl={10}
                placeholder="Search by title, views, or date…"
                defaultValue=""
                onChange={(e) => {
                  videoSearchRef.current = e.target.value;
                }}
                onKeyDown={(e) => e.key === "Enter" && handleVideoSearch()}
                {...searchInputStyles}
              />
            </InputGroup>
            <Button bg={C.primary} color="white" borderRadius="lg" px={5} _hover={{ bg: C.secondary }} onClick={handleVideoSearch}>
              Search
            </Button>
            {videoSearchTerm ? (
              <Button variant="outline" borderColor={C.border} color={C.secondary} borderRadius="lg" onClick={handleVideoClear}>
                Clear
              </Button>
            ) : null}
          </HStack>
          {videoSearchTerm ? (
            <Text fontSize="sm" color={C.muted} fontWeight="600">
              Found {filteredVideos.length} video{filteredVideos.length !== 1 ? "s" : ""} for &quot;{videoSearchTerm}&quot;
            </Text>
          ) : null}
        </VStack>

        <Box maxH="384px" overflowY="auto">
          <ItemList
            items={filteredVideos}
            renderItem={(video) => (
              <>
                <Box>
                  <Text fontWeight="800" color={C.secondary}>
                    {video.video?.title}
                  </Text>
                  <Text fontSize="sm" color={C.muted} mt={1}>
                    Views: {video.views_count} · {new Date(video.created_at).toLocaleDateString()}
                  </Text>
                </Box>
                <VStack align="end" spacing={1}>
                  <Badge
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    bg={video.seen ? "rgba(34, 84, 61, 0.12)" : "rgba(161, 98, 7, 0.15)"}
                    color={video.seen ? "#1e5c3a" : "#7a4a00"}
                  >
                    {video.total_played > 0 ? "Watched" : "Opened, not watched"}
                  </Badge>
                  {video.user_video_suspended ? (
                    <Badge borderRadius="full" px={2} py={0.5} bg="rgba(155, 44, 44, 0.12)" color="#9b2c2c">
                      Suspended
                    </Badge>
                  ) : null}
                </VStack>
              </>
            )}
            emptyMessage={videoSearchTerm ? "No videos match your search" : "No videos watched yet"}
            showAll
          />
        </Box>
      </SectionCard>
    );
  };

  const renderExams = () => (
    <SectionCard title="Exam history" icon={Award}>
      <ItemList
        items={exams}
        renderItem={(exam) => (
          <>
            <Box>
              <Text fontWeight="800" color={C.secondary}>
                {exam.name}
              </Text>
              <Text fontSize="sm" color={C.muted} mt={1}>
                {exam.question_number} questions · {exam.time ? `${exam.time} min` : "No time limit"}
              </Text>
              <Text fontSize="xs" color={C.muted} mt={1}>
                {new Date(exam.created_at).toLocaleDateString()}
              </Text>
            </Box>
            <VStack align="end" spacing={1}>
              <Badge
                borderRadius="full"
                px={2}
                py={0.5}
                bg={exam.published ? "rgba(34, 84, 61, 0.12)" : "rgba(69, 11, 17, 0.08)"}
                color={exam.published ? "#1e5c3a" : C.secondary}
              >
                {exam.published ? "Published" : "Draft"}
              </Badge>
              {exam.redo_flag ? (
                <Badge borderRadius="full" px={2} py={0.5} bg="rgba(91, 13, 22, 0.1)" color={C.primary}>
                  Retakeable
                </Badge>
              ) : null}
            </VStack>
          </>
        )}
        emptyMessage="No exams taken yet"
        showAll
      />
    </SectionCard>
  );

  const renderHomeworks = () => (
    <SectionCard title="Student homeworks" icon={BookOpen}>
      <Box maxH="384px" overflowY="auto">
        {student_homeworks && student_homeworks.length > 0 ? (
          <VStack align="stretch" spacing={4}>
            {student_homeworks.map((homework) => {
              const statusText =
                homework.status === 1 ? "Accepted" : homework.status === 2 ? "Refused" : "Pending";
              const statusTone =
                homework.status === 1 ? "ok" : homework.status === 2 ? "bad" : "warn";

              return (
                <Box
                  key={homework.id}
                  bg={C.surface}
                  border="1px solid"
                  borderColor={C.border}
                  borderRadius="lg"
                  p={4}
                  boxShadow="sm"
                  _hover={{ boxShadow: "md" }}
                >
                  <Flex align="flex-start" justify="space-between" gap={3} mb={3} flexWrap={{ base: "wrap", sm: "nowrap" }}>
                    <Box flex="1" minW={0}>
                      <Text fontWeight="800" color={C.secondary} mb={2}>
                        {homework.homework?.name || "Unknown homework"}
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        <Badge
                          borderRadius="full"
                          px={2}
                          py={0.5}
                          bg={
                            statusTone === "ok"
                              ? "rgba(34, 84, 61, 0.12)"
                              : statusTone === "bad"
                                ? "rgba(155, 44, 44, 0.12)"
                                : "rgba(161, 98, 7, 0.15)"
                          }
                          color={
                            statusTone === "ok" ? "#1e5c3a" : statusTone === "bad" ? "#9b2c2c" : "#7a4a00"
                          }
                        >
                          {statusText}
                        </Badge>
                        {homework.is_solved === 1 ? (
                          <Badge borderRadius="full" px={2} py={0.5} bg="rgba(91, 13, 22, 0.1)" color={C.primary}>
                            Solved
                          </Badge>
                        ) : null}
                        {homework.video_suspended === 1 ? (
                          <Badge borderRadius="full" px={2} py={0.5} bg="rgba(161, 98, 7, 0.2)" color="#5c3d00">
                            Video suspended
                          </Badge>
                        ) : null}
                      </HStack>
                    </Box>
                    <VStack align="end" spacing={0} fontSize="xs" color={C.muted} fontWeight="600">
                      <Text>Submitted: {new Date(homework.created_at).toLocaleDateString()}</Text>
                      <Text>Updated: {new Date(homework.updated_at).toLocaleDateString()}</Text>
                    </VStack>
                  </Flex>

                  {homework.file ? (
                    <Box mb={3}>
                      <Link
                        href={`${hwUrl}${homework.file}`}
                        isExternal
                        display="inline-flex"
                        alignItems="center"
                        gap={2}
                        fontWeight="700"
                        color={C.primary}
                        _hover={{ color: C.secondary, textDecoration: "underline" }}
                      >
                        <FileText size={16} />
                        View PDF
                      </Link>
                    </Box>
                  ) : null}

                  {homework.comment ? (
                    <Box mb={3} p={3} bg={C.canvas} borderRadius="md" border="1px solid" borderColor={C.border}>
                      <Text fontSize="xs" fontWeight="800" color={C.primary} mb={1}>
                        Student comment
                      </Text>
                      <Text fontSize="sm" color={C.secondary}>
                        {homework.comment}
                      </Text>
                    </Box>
                  ) : null}

                  {homework.assistant_comment ? (
                    <Box mb={3} p={3} bg="rgba(91, 13, 22, 0.06)" borderRadius="md" border="1px solid" borderColor={C.border}>
                      <Text fontSize="xs" fontWeight="800" color={C.primary} mb={1}>
                        Assistant comment
                      </Text>
                      <Text fontSize="sm" color={C.secondary} whiteSpace="pre-wrap">
                        {homework.assistant_comment}
                      </Text>
                    </Box>
                  ) : null}

                  {homework.evaluation ? (
                    <Box p={3} bg={C.surface} borderRadius="md" border="1px solid" borderColor="rgba(91, 13, 22, 0.25)">
                      <Text fontSize="xs" fontWeight="800" color={C.primary} mb={1}>
                        Evaluation
                      </Text>
                      <Text fontSize="sm" color={C.secondary} whiteSpace="pre-wrap">
                        {homework.evaluation}
                      </Text>
                    </Box>
                  ) : null}

                  {!homework.evaluation && !homework.assistant_comment && !homework.comment ? (
                    <Text fontSize="sm" color={C.muted} fontStyle="italic">
                      No comments or evaluations yet
                    </Text>
                  ) : null}
                </Box>
              );
            })}
          </VStack>
        ) : (
          <Box textAlign="center" py={10} color={C.muted}>
            <BookOpen size={48} style={{ margin: "0 auto 12px", opacity: 0.35 }} />
            <Text fontWeight="600">No homework submissions found</Text>
          </Box>
        )}
      </Box>
    </SectionCard>
  );

  const handleAddNote = useCallback(() => {
    if (!newNote.trim() || !student_id) return;
    dispatch(
      addStudentNoteRequest({
        student_id: student_id,
        note: newNote.trim(),
      })
    );
    setNewNote("");
  }, [newNote, student_id, dispatch]);

  const renderNotes = () => (
    <SectionCard title="Student notes" icon={StickyNote}>
      <Box mb={6} p={4} bg={C.surface} borderRadius="lg" border="1px solid" borderColor={C.border}>
        <VStack align="stretch" spacing={3}>
          <Text fontSize="sm" fontWeight="800" color={C.primary}>
            Add new note
          </Text>
          <Textarea
            value={newNote}
            placeholder="Enter your note here…"
            rows={3}
            disabled={isLoading}
            bg={C.canvas}
            borderColor={C.border}
            borderRadius="lg"
            _focusVisible={{
              borderColor: C.primary,
              boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.22)",
            }}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Flex justify="flex-end">
            <Button
              bg={C.primary}
              color="white"
              borderRadius="lg"
              px={6}
              isDisabled={!newNote.trim() || isLoading}
              isLoading={isLoading}
              _hover={{ bg: C.secondary }}
              onClick={handleAddNote}
            >
              Add note
            </Button>
          </Flex>
        </VStack>
      </Box>

      <VStack align="stretch" spacing={3}>
        {student_notes && student_notes.length > 0 ? (
          student_notes.map((note, index) => {
            const noteKey =
              note.id || (note.created_at ? `${note.created_at}-${index}` : `note-${index}`);
            return (
              <Box
                key={noteKey}
                p={4}
                bg={C.surface}
                border="1px solid"
                borderColor={C.border}
                borderRadius="lg"
                boxShadow="sm"
              >
                <Text color={C.secondary} whiteSpace="pre-wrap" fontWeight="600">
                  {note.note || note}
                </Text>
                {note.created_at ? (
                  <Text fontSize="xs" color={C.muted} mt={2} fontWeight="600">
                    {new Date(note.created_at).toLocaleString()}
                  </Text>
                ) : null}
                {note.user ? (
                  <Text fontSize="xs" color={C.muted} mt={1} fontWeight="600">
                    Added by: {note.user.name || note.user}
                  </Text>
                ) : null}
              </Box>
            );
          })
        ) : (
          <Box textAlign="center" py={10} color={C.muted}>
            <StickyNote size={48} style={{ margin: "0 auto 12px", opacity: 0.35 }} />
            <Text fontWeight="600">No notes yet</Text>
          </Box>
        )}
      </VStack>
    </SectionCard>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "payments":
        return renderPayments();
      case "attendance":
        return renderAttendance();
      case "videos":
        return renderVideos();
      case "exams":
        return renderExams();
      case "homeworks":
        return renderHomeworks();
      case "notes":
        return renderNotes();
      default:
        return renderOverview();
    }
  };

  const header = (
    <Flex align="center" justify="space-between" gap={4} flexWrap="wrap" w="100%">
      <VStack align="start" spacing={1}>
        <Heading
          as="h2"
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="800"
          color={C.primary}
          fontFamily="'Amiri','Tajawal',sans-serif"
        >
          Student report
        </Heading>
        <Text fontSize="xs" color={C.muted} textTransform="uppercase" letterSpacing="0.12em" fontWeight="700">
          Performance, billing, and activity
        </Text>
      </VStack>
      <HStack spacing={2} flexShrink={0}>
        <Button
          size="sm"
          bg={C.primary}
          color="white"
          borderRadius="lg"
          fontWeight="800"
          _hover={{ bg: C.secondary }}
          isDisabled={isLoading}
          onClick={() => dispatch(monthlyReportRequest({ student_id }))}
        >
          {isLoading ? "Sending…" : "Email monthly report"}
        </Button>
        <IconButton
          aria-label="Close report"
          icon={<X size={20} />}
          variant="ghost"
          color={C.secondary}
          borderRadius="full"
          _hover={{ bg: "rgba(91, 13, 22, 0.08)" }}
          onClick={closeFunc}
        />
      </HStack>
    </Flex>
  );

  const content = (
    <Box w="100%" dir="ltr">
      <Box borderBottom="1px solid" borderColor={C.border} bg={C.canvas} px={{ base: 2, md: 4 }} pt={2}>
        <Flex gap={1} overflowX="auto" pb={0} css={{ scrollbarWidth: "thin" }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                flexShrink={0}
                size="sm"
                leftIcon={<Icon size={16} />}
                variant="ghost"
                fontWeight="800"
                color={active ? C.primary : C.muted}
                bg={active ? C.surface : "transparent"}
                borderBottom="2px solid"
                borderBottomColor={active ? C.primary : "transparent"}
                mb="-1px"
                borderTopRadius="lg"
                borderBottomRadius={0}
                _hover={{ bg: active ? C.surface : "rgba(91, 13, 22, 0.06)" }}
              >
                {tab.label}
              </Button>
            );
          })}
        </Flex>
      </Box>

      <Box px={{ base: 4, md: 6 }} py={6} overflowY="auto" maxH="calc(90vh - 200px)">
        {isLoading ? (
          <Flex align="center" justify="center" py={16} direction="column" gap={4}>
            <Spinner thickness="3px" speed="0.65s" emptyColor={C.border} color={C.primary} size="xl" />
            <Text fontWeight="700" color={C.muted}>
              Loading report…
            </Text>
          </Flex>
        ) : (
          renderTabContent()
        )}
      </Box>

      {selectedData?.subject?.id == "34" && (
        <Box px={{ base: 4, md: 6 }} pb={6} pt={0} borderTop="1px solid" borderColor={C.border}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
            {selectedData?.profile_url ? (
              <Box>
                <Text fontSize="sm" fontWeight="800" color={C.primary} mb={2}>
                  Profile image 1
                </Text>
                <Image
                  src={`${baseUrl}/${selectedData.profile_url}`}
                  alt="Profile 1"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={C.border}
                  maxW="100%"
                  objectFit="contain"
                  bg={C.surface}
                />
              </Box>
            ) : null}
            {selectedData?.profile_url2 ? (
              <Box>
                <Text fontSize="sm" fontWeight="800" color={C.primary} mb={2}>
                  Profile image 2
                </Text>
                <Image
                  src={`${baseUrl}/${selectedData.profile_url2}`}
                  alt="Profile 2"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={C.border}
                  maxW="100%"
                  objectFit="contain"
                  bg={C.surface}
                />
              </Box>
            ) : null}
          </Grid>
        </Box>
      )}
    </Box>
  );

  return (
    <MainModel
      header={header}
      closeFunc={closeFunc}
      openVar={openUserData}
      buttonTitle={null}
      buttonFunc={null}
      size="6xl"
      hideDefaultFooter
      contentProps={{
        dir: "ltr",
        bg: C.canvas,
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "2xl",
        maxW: "min(1100px, 96vw)",
      }}
      headerProps={{
        px: { base: 4, md: 6 },
        py: 5,
        borderBottom: "1px solid rgba(216, 206, 195, 0.6)",
        bg: C.surface,
      }}
      bodyProps={{ p: 0 }}
    >
      {content}
    </MainModel>
  );
};

export default StudentReport;
